import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { Reveal, FloatingBlob, StaggerGroup, StaggerItem, AnimatedLine } from "@/components/Motion";
import { api } from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center relative">
        <FloatingBlob color="aurora-warm" size={400} top="10%" left="20%" delay={0} />
        <Reveal>
          <h1 className="font-heading text-4xl text-[var(--color-text)]">Article not found</h1>
          <Link to="/blog" className="btn-outline mt-8 inline-flex">Back to Blog</Link>
        </Reveal>
      </div>
    );
  }
  if (!post) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[var(--color-text-muted)]">Loading…</div>;
  }

  return (
    <article data-testid={`blog-post-${slug}`} className="relative overflow-hidden">
      <FloatingBlob color="aurora-purple" size={450} top="-5%" right="-10%" delay={0} />

      <section className="py-12 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <Link to="/blog" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-coral)] inline-flex items-center gap-2 transition-colors">
              <ArrowLeft className="w-4 h-4" /> All Articles
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="pb-12 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="overline mb-3 flex items-center gap-2"><Tag className="w-3 h-3 text-[var(--color-coral)]" /> {post.category}</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05]">{post.title}</h1>
            <div className="mt-6 flex items-center gap-5 text-sm text-[var(--color-text-muted)]">
              <span className="inline-flex items-center gap-2"><User className="w-3.5 h-3.5 text-[var(--color-coral)]" /> {post.author}</span>
              <span className="inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[var(--color-coral)]" /> {new Date(post.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-12 relative">
        <Reveal delay={0.2}>
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="aspect-[16/9] rounded-3xl overflow-hidden border border-[var(--color-border)]">
              {post.cover_image ? (
                <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
              ) : (
                <Placeholder variant="wide" label="Cover" className="!aspect-[16/9]" />
              )}
            </div>
          </div>
        </Reveal>
      </section>

      <AnimatedLine className="max-w-4xl mx-auto" />

      <section className="py-12 pb-24 relative">
        <FloatingBlob color="aurora-coral" size={400} bottom="10%" left="-5%" delay={1} />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10">
          <StaggerGroup className="prose prose-lg max-w-none" stagger={0.05}>
            {post.content.split(/\n+/).map((para, i) => (
              <StaggerItem key={i}>
                <p className="text-base md:text-lg text-[var(--color-text)]/85 leading-[1.75] mb-5">{para}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>
    </article>
  );
}
